import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';
import { IAMService, User, UserIdentifier } from '@vendasta/iamv2';
import { map } from 'rxjs/operators';
import { RouterModule } from '@angular/router';
import { AttendeeComponent } from './attendee/attendee.component';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';

interface UserContainer {
  user: User;
  isHost: boolean;
  isCreator: boolean;
}

@Component({
  selector: 'meeting-analysis-attendees',
  imports: [CommonModule, RouterModule, AttendeeComponent, MatChipsModule, TranslateModule],
  template: `
    @for (uc of userContainers$ | async; track uc.user?.userId) {
      <div class="attendee-item">
        <meeting-analysis-attendee [user]="uc.user" [large]="large()"></meeting-analysis-attendee>
        @if (uc.isHost) {
          <mat-chip>{{ 'COMMON.HOST' | translate }}</mat-chip>
        }
        @if (uc.isCreator) {
          <mat-chip>{{ 'COMMON.CREATOR' | translate }}</mat-chip>
        }
      </div>
    }
  `,
  styleUrls: ['./attendees.component.scss'],
})
export class AttendeesComponent {
  private iamService = inject(IAMService);
  creator = input<string>('');
  host = input<string>('');
  userIds = input<string[]>([]);
  large = input<boolean>(false);
  userContainers$ = toObservable(this.userIds).pipe(
    switchMap((userIds) => {
      if (userIds.length === 0) {
        return of([]);
      }
      const userIdentifiers = userIds.map((userId) => ({ userId }) as UserIdentifier);
      return this.iamService.getMultiUsers(userIdentifiers);
    }),
    map((users) => users.filter((user) => user !== null)),
    map((users) =>
      users.map(
        (user) =>
          ({
            user: user,
            isHost: user?.userId === this.host(),
            isCreator: user?.userId === this.creator(),
          }) as UserContainer,
      ),
    ),
  );
}
